/**
 * Format a numeric amount as currency. Defaults to USD/en-US locale. Whole
 * amounts render without decimals (retail UX in ARS/MXN markets doesn't quote
 * cents unless the user typed them); fractional amounts always show 2 digits
 * so cents entered in the amount field aren't silently rounded away.
 */
export const formatCurrency = (
  amount: number,
  currency: string,
  locale = 'en-US',
): string => {
  const isWhole = Number.isInteger(amount);
  const formatted = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: isWhole ? 0 : 2,
    maximumFractionDigits: isWhole ? 0 : 2,
  }).format(amount);
  return currency.toUpperCase() === 'USD' ? `${formatted} USD` : formatted;
};

export const formatNumber = (amount: number, locale = 'en-US'): string => {
  return new Intl.NumberFormat(locale, {
    maximumFractionDigits: 2,
  }).format(amount);
};

/**
 * Always render FX rates with exactly 4 decimal digits. FX rates are very
 * sensitive to rounding — even a third-decimal change shifts a $500 transfer
 * by tens of pesos — so we standardize on 4-digit precision everywhere they
 * appear in the UI.
 */
export const formatExchangeRate = (rate: number, locale = 'en-US'): string => {
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  }).format(rate);
};
